/**
 * Structure of a French national health identifier (NIR/NIA, 15 characters), position by position:
 * sex (1,2,7,8) / year (00-99) / month (01-12 or 20, unknown month) /
 * department (01-95, 2A, 2B, 97 DOM, 98 COM, 99 born abroad) / commune (000-999) /
 * order number (000-999) / control key (01-97). Overseas communes encode their department over the
 * first digit of the commune block (e.g. `971xx`), which is why the department group alone must not
 * be restricted to `01-95`.
 */
const NATIONAL_HEALTH_IDENTIFIER_PATTERN =
	/^([1278])(\d{2})(0[1-9]|1[0-2]|20)(0[1-9]|[1-8]\d|9[0-5]|2A|2B|97|98|99)(\d{3})(\d{3})(0[1-9]|[1-8]\d|9[0-7])$/;

/** Corsican departments are letters, not digits: substituted to keep the checksum body numeric. */
const CORSICAN_DEPARTMENT_SUBSTITUTIONS: Record<string, string> = {
	'2A': '19',
	'2B': '18',
};

/**
 * Strips whitespace and separators, uppercases letters. Serves validation only: the server, not the
 * screen, normalizes the value that gets stored, so this result is never written back to the field.
 */
function normalizeForValidation(value: string): string {
	return value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
}

/**
 * Validates the full 15-character structure and the modulo-97 control key of a French national
 * health identifier (NIR/NIA). Never throws: an ill-formed value, including one whose department
 * fails the Corsican substitution, simply fails and returns `false`.
 */
function hasValidNationalHealthIdentifierChecksum(value: string): boolean {
	const normalized = normalizeForValidation(value);
	const match = normalized.match(NATIONAL_HEALTH_IDENTIFIER_PATTERN);

	if (match === null) {
		return false;
	}

	const department = match[4];

	if (department === undefined) {
		return false;
	}

	const numericDepartment = CORSICAN_DEPARTMENT_SUBSTITUTIONS[department] ?? department;
	const checksumBody = `${normalized.slice(0, 5)}${numericDepartment}${normalized.slice(7, 13)}`;
	const bodyAsNumber = Number(checksumBody);

	if (Number.isNaN(bodyAsNumber)) {
		return false;
	}

	const providedControlKey = Number(normalized.slice(13, 15));
	const expectedControlKey = 97 - (bodyAsNumber % 97);

	return providedControlKey === expectedControlKey;
}

export default hasValidNationalHealthIdentifierChecksum;
